import axiosInterceptor from '../../utils/axios/axiosInterceptor';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dispatch, RootState } from '../store';
import { Dispatch } from 'redux';
import { stepsLayoutConf } from '../../constants/stepConf';
import { projectRunBasePath } from '../../services/projectRunsApi';
import { stepIdFromDashboardElementId } from '../../constants/runStepStatusKeys';
import {
  CompletionStatus,
  IStep,
  IStepLayoutConf,
  IStepsState,
  StepStatus,
  ValidationStatus,
} from '../../types/steps';

const api = () => axiosInterceptor(true);
enum FindTypeFlag {
  self = 'self',
  parent = 'parent',
}

interface UpdateIndexPayload {
  id: string;
}

interface UpdateStatusPayload {
  id: string;
  newStatus: ValidationStatus;
}

interface ProceedStepPayload {
  activeMainStepIndex: number;
  activeSubStepIndex: number;
}

// Creates an initial steps state object from the layout configuration
// Returns the root step state which contains main step and sub steps
// and with properties initialized
const getSteps = (layout?: IStepLayoutConf[], prefix: string = '') => {
  const response: IStep[] = [];
  layout?.forEach((e: IStepLayoutConf) => {
    const id = prefix !== '' ? prefix + '.' + e.step : e.step;
    const step: IStep = {
      id,
      labelId: `${id}.label`,
      descriptionId: `${id}.description`,
      completionStatus: CompletionStatus.none,
      validationStatus: ValidationStatus.none,
      steps: getSteps(e?.subSteps, id),
      activeStepIndex: 0,
    };
    response.push(step);
  });
  if (response[0]) {
    response[0].completionStatus = CompletionStatus.inProgress;
  }
  return response;
};
// Updates the completionStatus and validationStatus for each step
// Returns the root step state which contains main step and sub steps
const updateCompletionAndValidationStatus = (
  completionStatusMap: StepStatus,
  steps: IStep[]
): IStep[] => {
  steps.forEach((step) => {
    const id = step.id;
    const subStepsCompleted =
      step.steps &&
      step.steps.length > 0 &&
      step.steps.every(
        (subStep) =>
          completionStatusMap[`${subStep.id}`] === CompletionStatus.completed
      );
    step.completionStatus = subStepsCompleted
      ? CompletionStatus.completed
      : completionStatusMap[id] || CompletionStatus.none;

    if (step.completionStatus === CompletionStatus.completed) {
      step.validationStatus = ValidationStatus.valid;
    }

    if (step.steps) {
      updateCompletionAndValidationStatus(completionStatusMap, step.steps);
    }
  });
  return steps;
};

// Helper function that resets the completion and validation status for each step as none
const resetStepsCompletionStatus = (steps: IStep[]): IStep[] => {
  return steps.map((step) => {
    return {
      ...step,
      completionStatus: CompletionStatus.none,
      validationStatus: ValidationStatus.none,
      steps: step.steps ? resetStepsCompletionStatus(step.steps) : undefined,
    };
  });
};

//Helper function that returns the first step that has the completionStatus as none
const findFirstIncompleteStep = (completionStatusMap: StepStatus) => {
  const arr = Object.keys(completionStatusMap).sort();
  for (let i = 0; i < arr.length; ++i) {
    if (completionStatusMap[arr[i]] === CompletionStatus.none) return arr[i];
  }
  return arr[0];
};
const shouldUpdateValidationOnNext = [
  '1.b',
  '2.a',
  '3.a',
  '3.b',
  '3.c',
  '4.a',
  '4.b',
  '4.c',
  '5',
];

function stepCompletionForNext(
  stepId: string,
  validationStatus: ValidationStatus | undefined
): CompletionStatus {
  if (validationStatus === ValidationStatus.valid) {
    return CompletionStatus.completed;
  }
  if (shouldUpdateValidationOnNext.includes(stepId)) {
    return CompletionStatus.completed;
  }
  return CompletionStatus.none;
}

const initialMainSteps: IStepsState = {
  steps: getSteps(stepsLayoutConf),
  activeStepIndex: 0,
  isStepStatusLoading: false,
  isStepStatusSaving: false,
  errorMessage: '',
};

// Helper function that manages the active step
const setActiveStep = (id: string, state: IStepsState) => {
  let currentId = id;
  while (currentId) {
    const parentStep = findStep(currentId, state, FindTypeFlag.parent);
    if (!parentStep || !parentStep.steps) {
      break;
    }
    let index: number = -1;
    for (let i = 0; i < parentStep.steps.length; i++) {
      if (parentStep.steps[i].id === currentId) {
        index = i;
        break;
      }
    }
    if (index >= 0) {
      parentStep.activeStepIndex = index;
    } else {
      break;
    }
    currentId = parentStep.id;
  }
};

// Helper function that returns an array of traversal ids which
// can be used to traverse through and find a step from the Step State.
// If a step id "1.a" is given, returns an array [1, 1.a].
const getStepTraversalIds = (id: string) => {
  const splitIds = id.split('.');
  let idString: string = splitIds[0];
  const result = [idString];
  for (let i = 1; i < splitIds.length; i++) {
    idString += '.' + splitIds[i];
    result.push(idString);
  }
  return result;
};

// Returns the object from the Step State.
// If I give 1.b it can give the object 1.b or 1 based on if find parameter value
// Use case for getting parent is to update the active step index
// Use case for getting the object itself is to update the object state
const findStep = (
  id: string | string[],
  rootState: IStepsState,
  find: FindTypeFlag = FindTypeFlag.self
) => {
  let traversalIds = Array.isArray(id) ? id : getStepTraversalIds(id);
  traversalIds =
    find === 'parent'
      ? traversalIds.splice(0, traversalIds.length - 1)
      : traversalIds;
  let step: IStepsState | IStep | undefined = rootState;
  for (let i = 0; i < traversalIds.length; i++) {
    if (step?.steps) {
      step = step.steps.find((e: any) => e.id === traversalIds[i]);
    } else {
      step = undefined;
    }
  }
  return step as IStep | undefined;
};

export const stepsSlice = createSlice({
  name: 'steps',
  initialState: initialMainSteps,
  reducers: {
    setIsStepStatusLoading: (state, action: PayloadAction<boolean>) => {
      state.isStepStatusLoading = action.payload;
    },
    setIsStepStatusSaving: (state, action: PayloadAction<boolean>) => {
      state.isStepStatusSaving = action.payload;
    },
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    setUpdatedStep: (
      state,
      action: PayloadAction<{ completionStatusMap: StepStatus }>
    ) => {
      const completionStatusMap = action.payload.completionStatusMap;
      // Reset all activeStepIndex values to 0 before re-deriving the active
      // position from the API data. Without this, switching between runs leaves
      // stale sub-step indices from the previous run, causing the user to land
      // on the wrong sub-step (e.g. 2b instead of 2a).
      state.activeStepIndex = 0;
      state.steps.forEach((step) => {
        step.activeStepIndex = 0;
      });
      state.steps = updateCompletionAndValidationStatus(
        completionStatusMap,
        state.steps
      );
      const id = findFirstIncompleteStep(completionStatusMap);
      setActiveStep(id, state);
    },
    updateActiveStep: (state, action: PayloadAction<UpdateIndexPayload>) => {
      setActiveStep(action.payload.id, state);
    },
    updateValidationStatus: (
      state,
      action: PayloadAction<UpdateStatusPayload>
    ) => {
      const stepId = stepIdFromDashboardElementId(action.payload.id);
      const step = findStep(stepId, state);
      if (step) {
        step.validationStatus = action.payload.newStatus;
      }
    },

    // Proceeds to next and marks the current step
    proceedToNextStep: (state, action: PayloadAction<ProceedStepPayload>) => {
      const mainStep = state.steps[action.payload.activeMainStepIndex];
      const activeSubStepIndex = action.payload.activeSubStepIndex;
    if (Array.isArray(mainStep.steps)) {
      const subStepId = mainStep.steps[activeSubStepIndex]?.id;
      if (
        subStepId &&
        shouldUpdateValidationOnNext.includes(subStepId)
      ) {
        mainStep.steps[activeSubStepIndex].validationStatus =
          ValidationStatus.valid;
      }
    }
      if (Array.isArray(mainStep.steps)) {
        mainStep.steps[activeSubStepIndex].validationStatus ===
        ValidationStatus.valid
          ? (mainStep.steps[activeSubStepIndex].completionStatus =
              CompletionStatus.completed)
          : (mainStep.steps[activeSubStepIndex].completionStatus =
              CompletionStatus.none);
      }

      const allSubStepsValid =
        Array.isArray(mainStep.steps) &&
        mainStep.steps.every(
          (subStep) => subStep.validationStatus === ValidationStatus.valid
        );
      if (
        Array.isArray(mainStep.steps) &&
        mainStep.steps?.length - 1 > activeSubStepIndex
      ) {
        mainStep.activeStepIndex = Math.min(
          mainStep.activeStepIndex + 1,
          mainStep.steps.length - 1
        );
      } else {
        mainStep.completionStatus = allSubStepsValid
          ? CompletionStatus.completed
          : CompletionStatus.none;
        state.activeStepIndex = Math.min(
          state.activeStepIndex + 1,
          state.steps.length - 1
        );
        // Reset the new main step's sub-step index to 0 so we always start at
        // the first sub-step (e.g. 2a) rather than wherever a prior run left it.
        const nextMainStep = state.steps[state.activeStepIndex];
        if (nextMainStep) {
          nextMainStep.activeStepIndex = 0;
        }
      }
    },
    resetStepStatus: (state) => {
      state.steps = resetStepsCompletionStatus(state.steps);
    },
  },
});
export function fetchStepInfoAction(projectId: number, runId: number | null) {
  return async () => {
    dispatch(setIsStepStatusLoading(true));
    const base = projectRunBasePath(projectId, runId);
    if (!base) {
      dispatch(setIsStepStatusLoading(false));
      return;
    }
    try {
      const response = await api().get(`${base}/step-status`);
      const completionStatusMap = response.data.run_step_status;
      dispatch(
        setUpdatedStep({
          completionStatusMap,
        })
      );
    } catch (error: any) {
      if (error?.response?.status === 404) {
        dispatch(resetStepStatus());
      } else {
        dispatch(setErrorMessage(error.message));
      }
    } finally {
      dispatch(setIsStepStatusLoading(false));
    }
  };
}

export function updateStepInfoAction(
  activeSubStep: IStep,
  activeMainStepIndex: number,
  activeSubStepIndex: number,
  projectId: number,
  runId: number | null
) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    // Guard: drop repeated clicks while a previous update is still in flight.
    if (
      getState().stepsState.isStepStatusLoading ||
      getState().stepsState.isStepStatusSaving
    ) {
      return;
    }

    const base = projectRunBasePath(projectId, runId);
    if (!base) {
      return;
    }
    dispatch(setIsStepStatusSaving(true));
    try {
      const updatedStepStatus: StepStatus = {};
      const currentState = getState();
      const mainStep = currentState.stepsState.steps[activeMainStepIndex];
      const subStep = mainStep?.steps?.[activeSubStepIndex];
      const validationStatus =
        activeSubStep.validationStatus ?? subStep?.validationStatus;
      updatedStepStatus[activeSubStep.id] = stepCompletionForNext(
        activeSubStep.id,
        validationStatus
      );
      await api().put(`${base}/step-status`, updatedStepStatus);
      dispatch(proceedToNextStep({ activeMainStepIndex, activeSubStepIndex }));
    } catch (error: any) {
      dispatch(setErrorMessage(error.displayMessage));
    } finally {
      dispatch(setIsStepStatusSaving(false));
    }
  };
}

export function resetStepInfoAction(
  projectId: number,
  runId: number | null
) {
  return async (dispatch: Dispatch<any>, getState: () => RootState) => {
    const base = projectRunBasePath(projectId, runId);
    if (!base) {
      dispatch(setIsStepStatusLoading(false));
      return;
    }
    try {
      dispatch(resetStepStatus());
      const updatedStepStatus: StepStatus = {};
      const updatedState = getState();
      for (const step of updatedState.stepsState.steps) {
        if (step.steps) {
          for (const subStep of step.steps) {
            updatedStepStatus[subStep.id] = CompletionStatus.none;
          }
        }
      }
      await api().put(`${base}/step-status`, updatedStepStatus);
    } catch (error: any) {
      dispatch(setErrorMessage(error.displayMessage));
    } finally {
      dispatch(setIsStepStatusLoading(false));
    }
  };
}

export const {
  setIsStepStatusLoading,
  setIsStepStatusSaving,
  setErrorMessage,
  setUpdatedStep,
  updateActiveStep,
  updateValidationStatus,
  proceedToNextStep,
  resetStepStatus,
} = stepsSlice.actions;

export default stepsSlice.reducer;
